import { List, ListItem } from '@shopee/chatbot-ui';
import React, { useEffect, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import { TURN_PAGE_MODE } from '@shopee/chatbot-ui/lib/components/List';

import { TRACKING_MSG_TYPE, TRACKING_TYPE } from '@/utils/constants';
import { addObserver, removeObserver } from '@/utils/intersectionObserver';
import handleClickIntent from '@/utils/intent/handleClickIntent';
import sendBotMessageToAgent from '@/utils/sendBotMessageToAgent';
import { InitStoreType } from '@/types/store/init';
import { IntentType, IntentSource } from '@/types/intent';
import { trackingHandler } from '@/utils/btnClickHandler';
import { getChildInject } from '@/utils/instantLoaded/tools';
import { agentStore } from '@/stores';
import { ChatbotUIBasicProps } from '@/types/common';

import './style.scss';

const PREFIX = 'question-list';

interface QuestionsListProps {
  msgContent?: string;
  hotQuestions: Array<Question>;
  loading?: boolean;
  agentStore?: any;
  initStore?: InitStoreType;
  /** 是否是3x3 */
  isHeadIntent?: boolean;
  /** 是否是orderCard */
  isOrderCard?: boolean;
  /** 如果是3x3，传入一个页数 */
  categoryIndex?: number;
  categoryName: string;
  // 4-FAQ:recommender需要限制行数为3行/减小margintop
  source?: number;
  /** 是否是带有live button的Live Agent Recommender */
  hasLiveAgent?: boolean;
  /** 上一个dialogue ID */
  lastDialogueId?: string;
  isHistory?: boolean;
  // 区分埋点的index
  realIndex?: number;
  kbBotId?: number;
  kbBotName?: string;
  customCallback?: ChatbotUIBasicProps['customCallback'];
}

export interface Question {
  id: number;
  text: string;
  intentType: IntentType;
  intentSource: IntentSource;
  queryCallback: any;
}

interface QuestionItemProps {
  question: Question;
  index: number;
  showDivider: boolean;
  categoryName: string;
  loading?: boolean;
  onClick: () => void;
  source?: number;
  isOrderCard?: boolean;
  hasLiveAgent?: boolean;
  lastDialogueId?: string;
  isHistory?: boolean;
  kbBotId?: number;
  kbBotName?: string;
}

const QuestionItem = (props: QuestionItemProps) => {
  const questionRef = useRef(null);
  const trackdata: any = {
    content: props.question.text,
    question_content: props.question.text,
    question_id: props.question.id,
    is_order_card: Boolean(props.isOrderCard),
    if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
    is_ds_predicted: props.question.intentType === IntentType.Prediction,
    intent_source: props.question.intentSource,
    kb_intent_id: props.question.intentSource === IntentSource.KnowledgeBase ? props.question.id : undefined,
    bot_id: props.kbBotId,
    bot_name: props.kbBotName,
    question_category: props.categoryName,
    location: props.index,
    is_faq: false,
    is_liveAgentRecommender: props.hasLiveAgent ? 1 : 0,
    chat_dialogue_id: props.lastDialogueId ?? '',
    user_type: agentStore.userTypeName,
    agent_enquiry_category: agentStore.enquiryTypeName,
  };

  const trackingdata = JSON.stringify(trackdata);

  useEffect(() => {
    addObserver(questionRef.current);

    return () => removeObserver(questionRef.current);
  }, []);

  return (
    <div
      ref={questionRef}
      onClick={props.onClick}
      data-trackingname={TRACKING_TYPE.HOTQUESTION}
      data-trackingdata={trackingdata}
      data-trackingindex={props.index}
    >
      <ListItem content={props.question.text} className={`${PREFIX}-item`} />
      {props.showDivider && <div className={`${PREFIX}-divider`} />}
    </div>
  );
};

const QuestionsList = (props: QuestionsListProps) => {
  const {
    msgContent,
    hotQuestions,
    loading,
    categoryName,
    source,
    isOrderCard,
    hasLiveAgent,
    lastDialogueId = '',
    isHistory,
    kbBotId,
    kbBotName,
  } = props;
  const pageSize = 3;

  const handleQuestionClick = (question: Question, location: number) => {
    const { initStore, agentStore, categoryName, isOrderCard, hasLiveAgent, lastDialogueId = '', isHistory, customCallback = {} } = props;

    const trackingdata: any = {
      content: question.text,
      question_content: question.text,
      chat_session_id: initStore?.sessionId || '',
      question_id: question.id,
      is_order_card: Boolean(isOrderCard),
      if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
      is_ds_predicted: question.intentType === IntentType.Prediction,
      intent_source: question.intentSource,
      kb_intent_id: question.intentSource === IntentSource.KnowledgeBase ? question.id : undefined,
      bot_id: kbBotId,
      bot_name: kbBotName,
      question_category: categoryName,
      location: location,
      is_faq: false,
      is_liveAgentRecommender: hasLiveAgent ? 1 : 0,
      chat_dialogue_id: lastDialogueId,
      user_type: agentStore.userTypeName,
      agent_enquiry_category: agentStore.enquiryTypeName,
    };

    trackingHandler({
      trackingname: TRACKING_TYPE.HOTQUESTION,
      trackingdata,
    });

    // 在agent过程中，走agent逻辑
    if (agentStore.inAgent) {
      sendBotMessageToAgent({ content: question.text });
    } else {
      // 走ChatBot逻辑
      const params = { ...question.queryCallback, ...customCallback };
      handleClickIntent(question.text, params, (res: any) => {
        trackingHandler({
          trackingname: TRACKING_TYPE.HOTQUESTION,
          trackingdata: {...trackingdata, next_chat_dialogue_id: res.dialogue_id},
        });
      });
    }
  };

  return (
    <List pageSize={pageSize} title={msgContent} turnPageMode={TURN_PAGE_MODE.touchMove}>
      {hotQuestions.map((question: Question, index: number) => (
        <QuestionItem
          key={question.id}
          index={index}
          showDivider={(index + 1) % pageSize !== 0 && index !== hotQuestions.length - 1}
          categoryName={categoryName}
          question={question}
          loading={loading}
          source={source}
          isOrderCard={isOrderCard}
          hasLiveAgent={hasLiveAgent}
          lastDialogueId={lastDialogueId}
          isHistory={isHistory}
          onClick={() => handleQuestionClick(question, index)}
          kbBotId={kbBotId}
          kbBotName={kbBotName}
        />
      ))}
    </List>
  );
};

export default inject(getChildInject('initStore', 'agentStore'))(observer(QuestionsList));
